import { TicketCard } from './TicketCard';
import { EmptyState } from './ui/EmptyState';
import type { TicketDraft, UpdateTicketDraftInput } from '@pantheon/shared';

export interface TicketPlanViewProps {
  tickets: TicketDraft[];
  summary?: string;
  complexity?: string;
  onUpdateTicket: (draftId: string, input: UpdateTicketDraftInput) => void;
  readOnly?: boolean;
  className?: string;
}

export function TicketPlanView({ tickets, summary, complexity, onUpdateTicket, readOnly, className = '' }: TicketPlanViewProps) {
  if (tickets.length === 0) {
    return <EmptyState className={className} title="No tickets generated yet" hint="Describe what you want to build and Hermes will draft a plan." />;
  }

  const counts = ['epic', 'story', 'task', 'subtask', 'bug']
    .map((type) => {
      const n = tickets.filter((t) => t.type === type).length;
      return n > 0 ? `${n} ${type}${n > 1 ? (type === 'story' ? 'ies' : 's') : type === 'story' ? 'y' : ''}` : null;
    })
    .filter(Boolean);

  const complexityColors: Record<string, string> = {
    low: 'bg-success-light text-success',
    medium: 'bg-warning-light text-warning',
    high: 'bg-danger-light text-danger',
  };

  return (
    <div className={className}>
      {/* Summary */}
      <div className="bg-gray-50 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-semibold text-gray-900">{tickets.length} tickets</span>
            <span className="text-gray-500 text-sm ml-2">{counts.join(', ')}</span>
          </div>
          {complexity && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${complexityColors[complexity] ?? 'bg-gray-100 text-gray-600'}`}>
              {complexity} complexity
            </span>
          )}
        </div>
        {summary && <p className="text-sm text-gray-500 mt-2">{summary}</p>}
      </div>

      {/* Tickets */}
      <div className="space-y-2">
        {tickets
          .sort((a, b) => a.order - b.order)
          .map((ticket) => (
            <TicketCard key={ticket.draftId} ticket={ticket} onUpdate={onUpdateTicket} readOnly={readOnly} />
          ))}
      </div>
    </div>
  );
}
